import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { OffCanvasAnalytics } from './off-canvas.analytics';
import { StoreState } from '../../store/store.interface';
import { booleanConverter } from '../../common/converters';
import { connect } from 'pwa-helpers';
import { css, html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { getCloseOffCanvasAction, getOpenOffCanvasAction } from './store/off-canvas.selector';
import { isLastOffCanvas } from './utils/off-canvas.util';
import {
  OffCanvasAction,
  OffCanvasConfig,
  OffCanvasConfigs,
  OffCanvasInstance,
  OffCanvasInstances,
  OffCanvasTrigger,
} from './interfaces/off-canvas.interface';
import {
  triggerClearOffCanvas,
  triggerCloseOffCanvasDismiss,
  triggerFocusInOffCanvas,
  triggerFocusOutOffCanvas,
  triggerOpenOffCanvasDismiss,
} from './store/off-canvas.store';

@customElement('mhecomm-off-canvas')
export class OffCanvas extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .offcanvas-body {
        scrollbar-color: grey transparent;
      }

      .btn-close {
        margin-left: 50%;
      }

      @media (max-width: 576px) {
        .offcanvas.offcanvas-start.show {
          width: 100% !important;
        }
      }
    `,
  ];

  /**
   * An object containing key/value paired off-canvas configurations.
   */
  @property({ type: Object })
  offCanvasConfigs?: OffCanvasConfigs;

  /**
   * Whether to show the back button on the off-canvas header if there is a previous off-canvas.
   */
  @property({ converter: booleanConverter })
  showBackButton = true;

  /**
   * The trigger button configuration for the first off-canvas.
   */
  @property({ type: Object })
  trigger?: OffCanvasTrigger;

  /**
   * The list of corresponding off-canvas instances indexed by off-canvas ID.
   */
  private offCanvasInstances: OffCanvasInstances = {};

  /**
   * The ID of the remote trigger button if the off-canvas was opened remotely.
   */
  private remoteTriggerId = '';

  private shouldCloseAllOffCanvas = false;

  /**
   * Analytics data for the off-canvas.
   */
  private readonly analytics: OffCanvasAnalytics;

  private tooltips = [];

  constructor() {
    super();
    this.analytics = new OffCanvasAnalytics();
  }

  /**
   * Initializes the off-canvas event listeners.
   *
   * @param _changedProperties
   */
  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    setTimeout(this.updateOffCanvasConfigs.bind(this), 500);
  }

  /**
   * Cleans up the off-canvas event listeners.
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.updateOffCanvasConfigs(true);
  }

  /**
   * Opens or closes the off-canvas based on trigger states.
   *
   * if the off-canvas is the first (no previous trigger), we save the trigger ID for later use.
   *
   * @param state
   */
  stateChanged(state: StoreState): void {
    this.processOpenOffCanvasAction(getOpenOffCanvasAction(state));
    this.processCloseOffCanvasAction(getCloseOffCanvasAction(state));
  }

  renderComp(): TemplateResult {
    return html`
      ${Object.entries(this.offCanvasConfigs || {}).map(([offCanvasId, offCanvas], index) => {
        const offCanvasTitle = `${offCanvasId}-title`;

        return html`
          ${this.getOpenTrigger(index, offCanvas, this.trigger)}
          <div id="${offCanvasId}" class="offcanvas offcanvas-start" tabindex="-1" aria-labelledby="${offCanvasTitle}">
            <div id="${offCanvasId}-off-canvas-header" class="offcanvas-header border-bottom d-block pe-2">
              <div class="row g-0 m-0 w-100 align-items-center">
                ${this.getBackButton(offCanvas, index)}
                <div class="col ${index !== 0 && this.showBackButton ? 'col-9' : 'col-10'} align-content-center">
                  <span id="${offCanvasTitle}" class="offcanvas-title my-auto">
                    ${offCanvas?.title}
                  </span>
                </div>
                <div class="col col-2 g-0 text-end">
                  ${this.getCloseButton(offCanvas)}
                </div>
              </div>
              ${offCanvas.subtitle ?? nothing}
            </div>
            <div class="offcanvas-body ${offCanvas.bodyStyle}">
              <slot
                name="${offCanvasId}-off-canvas-body"
                @click=${(e: MouseEvent) => this.onBodyClick(e, offCanvas)}
              ></slot>
            </div>
          </div>
        `;
      })}
    `;
  }

  /**
   * Returns the back button for the nested off-canvas header.
   *
   * @param offCanvas
   * @param index
   */
  private getBackButton(offCanvas: OffCanvasConfig, index: number): TemplateResult | typeof nothing {
    if (index === 0 || !this.showBackButton) {
      return nothing;
    }

    return html`
      <div class="col col-1">
        <button
          id="${offCanvas.id}-back-button"
          data-automation-id="${offCanvas.id}-back-button"
          type="button"
          class="btn text-black p-0 me-2"
          @click=${this.onBackButtonClick.bind(this, offCanvas)}
          aria-label="Back"
          data-bs-toggle="tooltip"
          data-bs-trigger="hover"
          data-bs-placement="bottom"
          title="Back"
        >
          ${this.uxtIconReady ? html` <uxt-icon icon="chevron-left"></uxt-icon>` : nothing}
        </button>
      </div>
    `;
  }

  /**
   * Returns the close button for the off-canvas header.
   *
   * @param offCanvas
   */
  private getCloseButton(offCanvas: OffCanvasConfig): TemplateResult {
    return html`
      <button
        id="${offCanvas.id}-close-button"
        data-automation-id="${offCanvas.id}-close-button"
        type="button"
        class="btn-close text-reset m-1"
        @click=${this.closeAllOffCanvas.bind(this)}
        aria-label="Close"
        data-bs-dismiss="offcanvas"
        data-bs-toggle="tooltip"
        data-bs-trigger="hover"
        data-bs-placement="bottom"
        title="Close"
      ></button>
    `;
  }

  /**
   * Returns the open trigger button for the first off-canvas.
   *
   * @param index
   * @param offCanvas
   * @param trigger
   */
  private getOpenTrigger(
    index: number,
    offCanvas: OffCanvasConfig,
    trigger?: OffCanvasTrigger
  ): TemplateResult | typeof nothing {
    if (index !== 0 || !trigger) {
      return nothing;
    }

    return html`
      <button
        id="off-canvas-trigger"
        class="${trigger.style ? trigger.style : 'btn btn-primary'}"
        type="button"
        @click=${() => this.openOffCanvas(offCanvas)}
        data-bs-toggle="offcanvas"
      >
        ${trigger?.label}
        ${trigger?.icon
          ? html` <uxt-icon icon="${trigger.icon.name}" class="${trigger.icon?.style}"></uxt-icon>`
          : nothing}
      </button>
    `;
  }

  /**
   * Initializes or removes all declared off-canvas.
   */
  private updateOffCanvasConfigs(remove = false): void {
    for (const offCanvas of Object.values(this.offCanvasConfigs || {})) {
      const offCanvasElement: HTMLElement | null = this.getOffCanvasElement(offCanvas.id);
      if (!offCanvasElement) {
        console.error(`Could not initialise off-canvas ${offCanvas.id}.`);
        continue;
      }

      if (remove) {
        this.offCanvasInstances[offCanvas.id] = undefined;
        continue;
      }

      this.addOffCanvasEvents(offCanvas, offCanvasElement);
    }
  }

  /**
   * Initializes the off-canvas element and adds event listeners.
   *
   * Events listeners are remove automatically when the component is disconnected.
   *
   * @param offCanvas
   * @param element
   */
  addOffCanvasEvents(offCanvas: OffCanvasConfig, element?: HTMLElement): void {
    this.offCanvasInstances[offCanvas.id] = {
      bsInstance: this.bsInit(`#${offCanvas.id}`, window.bootstrap?.Offcanvas)?.instance,
      isOpen: false,
    };
    if (element) {
      this.addTrackedEventListener(element, 'hidden.bs.offcanvas', () => this.hideOffCanvas(offCanvas));
      this.addTrackedEventListener(element, 'shown.bs.offcanvas', () => this.focusOffCanvas(offCanvas));
    }
  }

  /**
   * Opens the off-canvas and focuses the element if it exists.
   *
   * Otherwise, it focuses the close button.
   *
   * @param offCanvas
   * @param reOpen
   */
  private openOffCanvas(offCanvas?: OffCanvasConfig, reOpen = false): void {
    const offCanvasElement: HTMLElement | undefined = offCanvas ? this.getOffCanvasElement(offCanvas.id) : undefined;
    if (!offCanvas || !offCanvasElement) {
      console.error('Off-canvas not found.');
      return;
    }

    const offCanvasInstance: OffCanvasInstance | undefined = this.offCanvasInstances[offCanvas.id];
    if (offCanvasInstance) {
      offCanvasInstance.bsInstance.show(offCanvasElement);
      offCanvasInstance.isOpen = true;
    }

    if (reOpen) {
      this.focusOffCanvas(offCanvas, true);
    }

    this.analytics.openEvent(this, offCanvas);
  }

  /**
   * Clears the off-canvas contents right before it is shown.
   *
   * @param offCanvas
   */
  private clearOffCanvas(offCanvas: OffCanvasConfig): void {
    store.dispatch(
      triggerClearOffCanvas({
        offCanvasId: offCanvas.id,
      })
    );
  }

  /**
   * Closes the current off-canvas.
   *
   * @param offCanvas
   */
  private closeOffCanvas(offCanvas?: OffCanvasConfig): void {
    if (!offCanvas) {
      console.error('Off-canvas not found.');
      return;
    }

    const offCanvasInstance: OffCanvasInstance | undefined = this.offCanvasInstances[offCanvas.id];
    if (offCanvasInstance) {
      offCanvasInstance.bsInstance.hide();
      offCanvasInstance.isOpen = false;
    }

    this.analytics.closeEvent(this, offCanvas);
  }

  /**
   * Closes all open off-canvas.
   */
  private closeAllOffCanvas(): void {
    this.shouldCloseAllOffCanvas = true;

    const offCanvasList: OffCanvasConfig[] = Object.values(this.offCanvasConfigs || {});

    for (const offCanvas of offCanvasList) {
      if (this.offCanvasInstances[offCanvas.id]?.isOpen) {
        this.closeOffCanvas(offCanvas);
      }
    }
    this.tooltips?.forEach((tooltip: any) => tooltip.instance.hide());
    this.allowBodyScroll();
  }

  /**
   * Focuses an element inside the off-canvas if it exists, otherwise it focuses the close button.
   *
   * It is called two ways: using bootstrap event when the off-canvas is shown, or to re-focus the previous off-canvas.
   *
   * @param offCanvas
   * @param reFocus
   */
  private focusOffCanvas(offCanvas: OffCanvasConfig, reFocus = false): void {
    if (offCanvas.focusElement) {
      store.dispatch(
        triggerFocusInOffCanvas({
          offCanvasId: offCanvas.id,
          focusElementSelector: offCanvas.focusElement,
        })
      );
      return;
    }

    if (reFocus) {
      setTimeout((): void => {
        this.getOffCanvasCloseButtonElement(offCanvas.id)?.focus();
      }, 200);
      return;
    }

    this.getOffCanvasCloseButtonElement(offCanvas.id)?.focus();
    this.disallowBodyScroll();
  }

  /**
   * Handles the hidden event for each off-canvas.
   *
   * If the off-canvas has a parent (i.e a previous trigger), it opens it, except if we want to close all off-canvas.
   *
   * If it is the first one (i.e previous trigger), it focuses the trigger button (either locally or remotely using redux).
   *
   * @param offCanvas
   */
  private hideOffCanvas(offCanvas: OffCanvasConfig): void {
    if (!this.shouldCloseAllOffCanvas && offCanvas.prevTrigger) {
      this.openOffCanvas(this.getOffCanvas(offCanvas.prevTrigger.offCanvasId), true);
    }

    if (!offCanvas.prevTrigger) {
      if (this.trigger) {
        (this.shadowRoot?.querySelector('#off-canvas-trigger') as HTMLElement)?.focus();
        return;
      }

      store.dispatch(
        triggerFocusOutOffCanvas({
          offCanvasId: offCanvas.id,
          focusElementSelector: this.remoteTriggerId,
        })
      );
    }

    this.clearOffCanvas(offCanvas);
    if (this.shouldCloseAllOffCanvas && isLastOffCanvas(offCanvas.id, this.offCanvasConfigs)) {
      this.shouldCloseAllOffCanvas = false;
    }

    this.allowBodyScroll();
  }

  /**
   * Handles the back button click event.
   *
   * @param offCanvas
   */
  private onBackButtonClick(offCanvas: OffCanvasConfig): void {
    this.shouldCloseAllOffCanvas = false;
    this.closeOffCanvas(offCanvas);
  }

  /**
   * Events triggered when the nested body slot is clicked.
   *
   * If the ID of the element is the next off-canvas trigger, it opens the next off-canvas if it exists.
   *
   * @param event
   * @param offCanvas
   *
   * @deprecated listening to body click events on children slots does not work with shadow DOM. This will be removed.
   */
  private onBodyClick(event: MouseEvent, offCanvas: OffCanvasConfig): void {
    const elementId: string | undefined = (event?.target as Element)?.id;

    if (elementId === offCanvas.closeTriggerId) {
      this.closeOffCanvas(offCanvas);
      return;
    }

    for (const trigger of offCanvas.nextTriggers ?? []) {
      if (elementId === trigger.triggerId) {
        this.openOffCanvas(this.getOffCanvas(trigger.offCanvasId));
        return;
      }
    }
  }

  /**
   * Retrieves the off-canvas configuration based on the off-canvas ID.
   *
   * @param offCanvasId
   */
  private getOffCanvas(offCanvasId?: string): OffCanvasConfig | undefined {
    if (!offCanvasId || !this.offCanvasConfigs) {
      return undefined;
    }

    return this.offCanvasConfigs[offCanvasId];
  }

  /**
   * Retrieves the off-canvas element from the shadow DOM.
   *
   * @param offCanvasId
   */
  private getOffCanvasElement(offCanvasId: string): HTMLElement {
    return this.shadowRoot?.querySelector(`#${offCanvasId}`) as HTMLElement;
  }

  /**
   * Retrieves the close button from the shadow DOM.
   */
  private getOffCanvasCloseButtonElement(offCanvasId: string): HTMLElement {
    return this.shadowRoot?.querySelector(`#${offCanvasId}-close-button`) as HTMLElement;
  }

  /**
   * Processes the open off-canvas action.
   *
   * If the first off-canvas is opened, it saves the trigger ID for later use.
   *
   * @param action
   */

  private processOpenOffCanvasAction(action?: OffCanvasAction): void {
    if (!action) {
      return;
    }

    const offCanvas: OffCanvasConfig | undefined = this.getOffCanvas(action.offCanvasId);
    if (!offCanvas) {
      return;
    }

    if (!offCanvas.prevTrigger) {
      this.remoteTriggerId = action.triggerId ?? '';
    }

    this.openOffCanvas(offCanvas);
    store.dispatch(triggerOpenOffCanvasDismiss());
  }

  /**
   * Processes the open off-canvas action.
   *
   * If the first off-canvas is opened, it saves the trigger ID for later use.
   *
   * @param action
   */
  private processCloseOffCanvasAction(action?: OffCanvasAction): void {
    if (!action) {
      return;
    }

    const offCanvas: OffCanvasConfig | undefined = this.getOffCanvas(action.offCanvasId);
    if (!offCanvas) {
      return;
    }

    this.closeOffCanvas(offCanvas);
    store.dispatch(triggerCloseOffCanvasDismiss());
  }

  /**
   * Initializes the tooltip on the matching buttons.
   */
  updated(): void {
    this.tooltips = this.bsInitAll('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }

  /*
   * Sets body position to fixed and width of the body to 100%
   */
  disallowBodyScroll() {
    document.querySelector('body')!.style.position = 'fixed';
    document.querySelector('body')!.style.width = '100%';
  }

  /*
   * Reverts back the position and the width of the body
   */
  allowBodyScroll() {
    document.querySelector('body')!.style.position = 'static';
    document.querySelector('body')!.style.width = 'auto';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-off-canvas': OffCanvas;
  }
}
